import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from 'components/CncUI/Typography';
import Loader from 'components/Loader';

import LoanOutPanel from './LoanOutPanel';
import HistorySection from './Sidebar/HistorySection';
import SelectLoanOut from './SelectLoanOut';

import useQuery from 'hooks/useQuery';
import useMutation from 'hooks/useMutation';
import useData from 'hooks/useData';
import Logger from 'utilities/logger';
import DirectDepositPanel from './DirectDeposit';

const useStyles = makeStyles(({ breakpoints }) => ({
  header: {
    marginTop: 40,
    marginBottom: 16,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
}));

const Loanout = ({ offer }) => {
  const classes = useStyles();
  const [loanout, setLoanout] = useState(null);
  const [existing, setExisting] = useState([]);
  const [documents, setDocuments] = useState(null);
  const [state, modify] = useData({
    edit: false,
    loading: true,
    saving: false,
    reload: true,
    reloadHistory: true,
    loadingHistory: true,
    showExisting: false,
  });
  const getLoanout = useQuery('getLoanout', {
    fetchPolicy: 'network-only',
    scopes: 'payroll_employee:edit',
    response: res => res.data.getLoanout || null,
  });
  const saveExistingLoanout = useMutation('saveExistingLoanout', {
    scopes: 'payroll_employee:edit',
    request: input => ({ input }),
    response: res => res.data.saveExistingLoanout || false,
  });
  const getExistingLoanouts = useQuery('getExistingLoanouts', {
    fetchPolicy: 'network-only',
    scopes: 'payroll_employee:edit',
    response: res => res.data.getExistingLoanouts || [],
  });
  const getHistory = useQuery('getLoanoutHistory', {
    fetchPolicy: 'network-only',
    scopes: 'payroll_employee:edit',
    response: res => {
      const list = res.data.getLoanoutHistory || [];
      const results = { W9: [], LOI: [] };
      list.forEach(item => results[item.template].push(item));
      return results;
    },
  });
  const logger = Logger('Loanouts');

  useEffect(async () => {
    if (state.reload) {
      try {
        modify({ loading: true, reload: false });
        const data = await getLoanout();
        if (data) {
          setLoanout(data);
        } else {
          const existing = await getExistingLoanouts();
          setExisting(existing);
          modify({ showExisting: existing.length > 0 });
        }
        modify({ loading: false });
      } catch (err) {
        logger.error(err);
        modify({ loading: false });
      }
    }
  }, [state.reload]);

  useEffect(() => {
    if (state.reloadHistory) {
      modify({ loadingHistory: true, reloadHistory: false });
      getHistory()
        .then(data => setDocuments(data))
        .catch(err => logger.error(err))
        .finally(() => modify({ loadingHistory: false }));
    }
  }, [state.reloadHistory]);

  if (state.loading) {
    return <Loader />;
  }

  return (
    <Grid container spacing={3} id="AdminDashboard">
      <Grid item xs={12}>
        <Typography variant="h2" id="HeaderTitle" className={classes.header}>
          Loan-out
        </Typography>
      </Grid>
      <Grid item xs={12} lg={8}>
        <LoanOutPanel
          state={state}
          offer={offer}
          loanout={loanout}
          updateParent={modify}
          existing={existing}
        />
      </Grid>
      <Grid item xs={12} lg={4}>
        {state.loadingHistory ? (
          <Loader />
        ) : (
          <HistorySection list={documents} />
        )}
      </Grid>
      {loanout && (
        <Grid item xs={12} lg={8}>
          <DirectDepositPanel currentLoanout={loanout} />
        </Grid>
      )}

      <SelectLoanOut
        state={state}
        existing={existing}
        availableLoanOuts={existing}
        updateParent={modify}
        onSave={saveExistingLoanout}
      />
    </Grid>
  );
};

export default Loanout;
